import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus, MessageCircle } from 'lucide-react';
import { useInView } from '../../hooks/useAnimations';

const faqs = [
  { question: 'How quickly can I get started with TeleCRM?', answer: 'You can set up TeleCRM in under 10 minutes. Simply sign up, import your leads, and start making calls. Our onboarding team is available to help you get the most out of the platform.' },
  { question: 'Can I integrate TeleCRM with my existing tools?', answer: 'Yes! TeleCRM integrates with popular tools like Slack, Zapier, Google Workspace, and more. Our API allows custom integrations with virtually any business application.' },
  { question: 'Is my data secure with TeleCRM?', answer: 'Absolutely. We use bank-grade encryption, comply with GDPR and SOC 2 standards, and offer role-based access controls. Your data is stored securely in certified data centers.' },
  { question: 'What kind of support do you offer?', answer: 'All plans include email support. Professional plans get priority support with faster response times. Enterprise customers receive 24/7 dedicated support and a dedicated customer success manager.' },
  { question: 'Can I upgrade or downgrade my plan?', answer: 'Yes, you can change your plan at any time. Upgrades take effect immediately, and you\'ll only pay the prorated difference. Downgrades apply at the start of your next billing cycle.' },
  // { question: 'Do you offer a free trial?', answer: 'Yes! We offer a 14-day free trial on all plans with no credit card required. You get full access to all features during the trial period.' },
];

const FAQItem = ({ faq, isOpen, onToggle, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.1 }}
    className="border-b border-purple-100 last:border-0"
  >
    <button
      onClick={onToggle}
      className="w-full py-6 flex items-start justify-between gap-4 text-left group"
    >
      <span className="text-lg font-medium text-slate-900 group-hover:text-purple-600 transition-colors">
        {faq.question}
      </span>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
        isOpen ? 'bg-purple-600 rotate-0' : 'bg-purple-50 group-hover:bg-purple-100'
      }`}>
        {isOpen ? (
          <Minus className="w-5 h-5 text-white" />
        ) : (
          <Plus className="w-5 h-5 text-purple-600" />
        )}
      </div>
    </button>

    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
        >
          <p className="pb-6 text-slate-600 leading-relaxed">{faq.answer}</p>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

const FAQ = () => {
  const [ref, isInView] = useInView({ threshold: 0.1 });
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section id="faq" ref={ref} className="py-24 relative overflow-hidden bg-gradient-to-b from-white to-slate-50">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
          className="absolute top-1/4 left-0 w-[400px] h-[400px] rounded-full orb-purple"
        />
      </div>

      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 mb-6"
          >
            <MessageCircle className="w-4 h-4 text-purple-600" />
            <span className="text-sm font-medium text-purple-700">FAQ</span>
          </motion.div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>
            Frequently Asked{' '}
            <span className="text-gradient">Questions</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Everything you need to know about TeleValley.
          </p>
        </motion.div>

        {/* FAQ Items */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl border border-purple-100 shadow-lg shadow-purple-500/5 overflow-hidden"
        >
          <div className="p-6 md:p-8">
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                faq={faq}
                isOpen={openIndex === index}
                onToggle={() => setOpenIndex(openIndex === index ? -1 : index)}
                index={index}
              />
            ))}
          </div>
        </motion.div>

        {/* Contact CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5 }}
          className="text-center mt-12"
        >
          <p className="text-slate-600 mb-4">Still have questions?</p>
          <a
            href="mailto:contact@televalley.com"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 text-purple-700 font-medium hover:bg-purple-100 transition-colors"
          >
            <MessageCircle className="w-5 h-5" />
            Contact our support team
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;